import React, { useState } from "react";
import { toast } from "sonner";
import { Loader2, ArrowRight } from "lucide-react";
import { createBooking } from "@/lib/api";
import { SERVICE_TYPES } from "@/data";

const field =
  "w-full bg-transparent border-b border-white/15 focus:border-primary outline-none py-3 text-sm text-white placeholder:text-zinc-500 transition-colors duration-300";

const empty = {
  full_name: "",
  phone: "",
  email: "",
  pickup: "",
  dropoff: "",
  date: "",
  time: "",
  service_type: SERVICE_TYPES[0],
  passengers: "",
  notes: "",
};

export const BookingForm = ({ compact = false }) => {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.phone || !form.pickup || !form.dropoff) {
      toast.error("Please fill in your name, phone, pickup and drop-off.");
      return;
    }
    setLoading(true);
    try {
      await createBooking(form);
      toast.success("Ride requested. Our dispatch team will confirm shortly.");
      setForm(empty);
    } catch {
      toast.error("Something went wrong. Please call us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="booking-form" className="space-y-5">
      <div className="grid grid-cols-2 gap-4">
        <input data-testid="booking-name" className={field} placeholder="Full Name" value={form.full_name} onChange={set("full_name")} />
        <input data-testid="booking-phone" className={field} placeholder="Phone" value={form.phone} onChange={set("phone")} />
      </div>
      <input data-testid="booking-email" className={field} placeholder="Email (optional)" value={form.email} onChange={set("email")} />
      <div className="grid grid-cols-2 gap-4">
        <input data-testid="booking-pickup" className={field} placeholder="Pickup Location" value={form.pickup} onChange={set("pickup")} />
        <input data-testid="booking-dropoff" className={field} placeholder="Drop-off Location" value={form.dropoff} onChange={set("dropoff")} />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <input data-testid="booking-date" type="date" className={`${field} [color-scheme:dark]`} value={form.date} onChange={set("date")} />
        <input data-testid="booking-time" type="time" className={`${field} [color-scheme:dark]`} value={form.time} onChange={set("time")} />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <select data-testid="booking-service" className={`${field} cursor-pointer`} value={form.service_type} onChange={set("service_type")}>
          {SERVICE_TYPES.map((s) => (
            <option key={s} value={s} className="bg-zinc-900">{s}</option>
          ))}
        </select>
        <input data-testid="booking-passengers" className={field} placeholder="Passengers" value={form.passengers} onChange={set("passengers")} />
      </div>
      {!compact && (
        <input data-testid="booking-notes" className={field} placeholder="Special requests (optional)" value={form.notes} onChange={set("notes")} />
      )}
      <button
        type="submit"
        data-testid="booking-submit"
        disabled={loading}
        className="group mt-2 flex w-full items-center justify-center gap-2 bg-primary py-4 text-xs font-medium uppercase tracking-[0.2em] text-primary-foreground transition-all duration-300 hover:tracking-[0.3em] disabled:opacity-60"
      >
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Request My Ride"}
        {!loading && <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />}
      </button>
    </form>
  );
};
